/* @flow */
import gql from 'graphql-tag';
import {compose} from 'redux';
import React, {PureComponent, Fragment} from 'react';
import {connect} from 'react-redux';
import {FormattedMessage, injectIntl} from 'react-intl';
import {ButtonPrimary} from '@homeaway/react-buttons';
import {edapTriggerPriceSummaryDisplay} from '@homeaway/react-price-summary';
import ExpandedSearchGroupHitPlaceholder from './ExpandedSearchGroupHitPlaceholder';
import Hit, {fragments as hitFragments} from '../Hit/Hit';
import {trigger as edapTrigger} from '../../utils/edapWrapper';
import mapActions from '../../actions/map';
import expandedSearchGroupsActions from '../../actions/expandedSearchGroups';

import type {IntlShape} from 'react-intl';
import type {Shape as HitShape} from '../Hit/Hit';
import type {State as ReduxState} from '../../reducers';
import type {MapViewport} from '../Map/Map';

export const fragments = {
    expandedSearchGroup: gql`
        fragment ExpandedSearchGroupExpandedSearchGroup on ExpandedSearchGroup {
            hits {
                ...HitSERPHit
            }
            mapViewport {
                neLat
                neLong
                swLat
                swLong
            }
        }
        ${hitFragments.SERPHit}
    `
};

export type Shape = {
    type?: string,
    label?: string,
    hits: HitShape[],
    mapViewport: MapViewport
};

type StateProps = {
    isExpanded: boolean
};

type DispatchProps = {
    syncBounds: typeof mapActions.syncBounds,
    expandGroup: typeof expandedSearchGroupsActions.expandGroup
};

type OwnProps = {
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: (string) => void,
    expandedSearchGroup: Shape,
    index: number,
    isDatelessSearch: boolean
};

type Props = DispatchProps & OwnProps & StateProps & {intl: IntlShape};

const handleHitClick = ({badges, listingId, priceSummary}: HitShape) => {
    const options = {delayProcessing: true};
    edapTrigger(
        'search.hit.click',
        {
            eventlistingids: listingId,
            badges: (badges || []).join(',')
        },
        options
    );
    if (priceSummary) {
        edapTriggerPriceSummaryDisplay(priceSummary, options);
    }
};

export class ExpandedSearchGroup extends PureComponent<Props> {
    expand = () => {
        const {
            expandedSearchGroup: {mapViewport},
            index,
            syncBounds,
            expandGroup
        } = this.props;
        if (mapViewport) {
            syncBounds({...mapViewport}, {fitBoundsOverride: true});
            expandGroup({index, hasModifiedMapViewport: true});
        } else {
            expandGroup({index});
        }
    };

    render() {
        const {
            expandedSearchGroup: {hits, label},
            intl,
            isDatelessSearch,
            isExpanded,
            onHitMouseEnter,
            onHitMouseLeave
        } = this.props;
        const message = label || (
            <FormattedMessage
                id="expandedSearchNearbyLabel"
                defaultMessage="You might like these nearby properties"
            />
        );

        const buttonLabel = intl.formatMessage(
            {
                id: 'expandedSearchNearbyButtonLabel',
                defaultMessage:
                    'View {count, plural, one {{count} nearby property} few {{count} nearby properties} many {{count} nearby properties} other {{count} nearby properties}}'
            },
            {
                count: hits.length
            }
        );
        return (
            <Fragment>
                <dt className={'ExpandedSearchGroup__label'}>{message}</dt>
                <dd className="ExpandedSearchGroup__content">
                    {isExpanded ? (
                        <div className="ExpandedSearchGroup__hits">
                            {hits.map((hit) => (
                                <Hit
                                    {...{
                                        key: hit.listingId,
                                        hit,
                                        isDatelessSearch,
                                        onHitClick: handleHitClick,
                                        onMouseEnter: onHitMouseEnter,
                                        onMouseLeave: onHitMouseLeave
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="ExpandedSearchGroup__card">
                            <div className={'ExpandedSearchGroup__button'}>
                                <ButtonPrimary
                                    onClick={this.expand}
                                    label={buttonLabel}
                                />
                            </div>
                            <ExpandedSearchGroupHitPlaceholder
                                hitData={hits[0]}
                            />
                        </div>
                    )}
                </dd>
            </Fragment>
        );
    }
}

const mapStateToProps = (
    {expandedSearchGroups: {openGroupIndexes}}: ReduxState,
    {index}: OwnProps
) => ({
    isExpanded: openGroupIndexes.includes(index)
});

const mapDispatchToProps = {
    expandGroup: expandedSearchGroupsActions.expandGroup,
    syncBounds: mapActions.syncBounds
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(ExpandedSearchGroup);
